import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Skeleton,
} from "@mui/material";
import axios from "axios";

// Blockchain Services & Web3
import cinstance from "../../../Service/randomcinstance";
import web3 from "../../../Service/web3";
import {
  networkID,
  sumWeiToEther,
  findLowestWeiToEther,
  compareWei,
} from "../../../../static/utils";
import { getNetworkCurrency, DEFAULT_CURRENCY } from "../../../../static/Constants";

// Theme & Common UI
import NavBar from "../../../Components/NavBar";
import { colors, borderRadiusPx } from "../../../../theme";

// Subcomponents
import MegaHeroBanner from "./components/MegaHeroBanner";
import MegaLotteryFilters from "./components/MegaLotteryFilters";
import MegaLotteryCard from "./components/MegaLotteryCard";
import MegaLotteryEmptyState from "./components/MegaLotteryEmptyState";
import MegaHowToPlay from "./components/MegaHowToPlay";

import "./MegaLotteryStyle.css";

const withTimeout = (promise, ms = 4500) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("RPC Request Timeout")), ms)
    ),
  ]);
};

const isRolloverStatus = (lottery) => {
  if (!lottery) return false;
  const statusId = String(lottery.status);
  return (
    statusId === "3" ||
    statusId === "5" ||
    Boolean(lottery.rolloverperct && Number(lottery.rolloverperct) > 0)
  );
};

const MegaLottery = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [networkId, setNetworkId] = useState(networkID);
  const [lotteryList, setLotteryList] = useState([]);
  const [partnerMap, setPartnerMap] = useState({});
  const [partnerLogoData, setPartnerLogoData] = useState([]);

  // Filters & Sorting
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "open" | "rollover"
  const [sortBy, setSortBy] = useState("newest");

  const apiUrl = process.env.REACT_APP_API_KEY;
  const currency = useMemo(
    () => getNetworkCurrency(networkId) || DEFAULT_CURRENCY,
    [networkId]
  );

  /**
   * Fetch All Live & Active MegaBET / MRL (Type 2) Lotteries
   */
  const fetchMegaLotteries = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const watchdog = setTimeout(() => {
        setLoading(false);
        setRefreshing(false);
      }, 7000);

      try {
        if (window.ethereum) {
          window.web3 = web3;
          if (window.ethereum.networkVersion) {
            setNetworkId(window.ethereum.networkVersion);
          } else if (web3 && web3.eth && web3.eth.net && web3.eth.net.getId) {
            withTimeout(web3.eth.net.getId(), 2000)
              .then((netId) => {
                if (netId) setNetworkId(netId);
              })
              .catch(() => {});
          }
        }

        // 1. Get total lottery IDs count
        const totalIdsRaw = await withTimeout(
          cinstance.methods.lotteryId().call(),
          4000
        ).catch((err) => {
          console.warn("[MegaBET] lotteryId() call failed:", err);
          return 0;
        });

        const totalIds = Number(totalIdsRaw || 0);
        const lotteryCalls = [];

        // Lotteries are indexed from 1 up to totalIds
        for (let i = 1; i <= totalIds; i++) {
          lotteryCalls.push(
            withTimeout(cinstance.methods.lottery(i).call(), 4000).catch((err) => {
              console.log("lottery", i, err);
              return null;
            })
          );
        }

        const lotteryResults = await Promise.all(lotteryCalls);

        // Filter strictly for MegaBET lotteries (Type 2) with active status (0, 3, or 5)
        const filteredLotteries = lotteryResults
          .filter(
            (response) =>
              response &&
              (response.status === "0" ||
                response.status === "3" ||
                response.status === "5") &&
              Number(response.lotteryType) === 2
          )
          .sort((a, b) => Number(b.lotteryId) - Number(a.lotteryId));

        setLotteryList(filteredLotteries);

        // 2. Fetch Partner profiles concurrently for unique valid addresses
        const uniquePartners = [
          ...new Set(
            filteredLotteries
              .map((item) => item.partnerAddress || item.partner)
              .filter(
                (addr) =>
                  addr &&
                  addr !== "0x0000000000000000000000000000000000000000" &&
                  addr !== "0x0"
              )
          ),
        ];

        if (uniquePartners.length > 0) {
          const partnerPromises = uniquePartners.map((addr) =>
            withTimeout(cinstance.methods.partnerbyaddr(addr).call(), 3500)
              .then((res) => ({ [addr]: res }))
              .catch((err) => {
                console.log("partnerbyaddr error", err);
                return { [addr]: null };
              })
          );

          const partnerEntries = await Promise.all(partnerPromises);
          const combinedPartners = partnerEntries.reduce(
            (acc, curr) => ({ ...acc, ...curr }),
            {}
          );
          setPartnerMap(combinedPartners);
        }
      } catch (err) {
        console.log("MegaLottery load error:", err);
      } finally {
        clearTimeout(watchdog);
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  /**
   * Fetch Partner Logos from AWS S3 API
   */
  useEffect(() => {
    let isCancelled = false;
    if (apiUrl) {
      axios
        .get(`${apiUrl}/s3imageupload/getAllPartnerLogos`)
        .then((response) => {
          if (!isCancelled) {
            const data = response?.data?.result || response?.data || [];
            setPartnerLogoData(Array.isArray(data) ? data : []);
          }
        })
        .catch((err) => {
          console.warn("[MegaBET] Partner logos fetch failed:", err);
        });
    }
    return () => {
      isCancelled = true;
    };
  }, [apiUrl]);

  useEffect(() => {
    fetchMegaLotteries();
  }, [fetchMegaLotteries]);

  /**
   * Statistics Calculated from Live MegaBET Lotteries
   */
  const totalActive = lotteryList.length;

  const combinedPrize = useMemo(() => {
    return sumWeiToEther(lotteryList.map((item) => item.totalPrize), 5);
  }, [lotteryList]);

  const lowestEntry = useMemo(() => {
    return findLowestWeiToEther(lotteryList.map((item) => item.entryFee), 3);
  }, [lotteryList]);

  const rolloverCount = useMemo(() => {
    return lotteryList.filter((item) => isRolloverStatus(item)).length;
  }, [lotteryList]);

  const openCount = useMemo(() => {
    return lotteryList.filter((item) => !isRolloverStatus(item)).length;
  }, [lotteryList]);

  /**
   * Filtered & Sorted MegaBET Lotteries
   */
  const displayedLotteries = useMemo(() => {
    let result = [...lotteryList];

    // 1. Status Filter
    if (statusFilter === "rollover") {
      result = result.filter((item) => isRolloverStatus(item));
    } else if (statusFilter === "open") {
      result = result.filter((item) => !isRolloverStatus(item));
    }

    // 2. Search Query Filter
    if (searchQuery && searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter((item) => {
        const idMatch = String(item.lotteryId || "").includes(q);
        const addrMatch = String(item.partnerAddress || item.partner || "")
          .toLowerCase()
          .includes(q);
        const partner = partnerMap[item.partnerAddress || item.partner];
        const partnerNameMatch =
          partner?.name?.toLowerCase().includes(q) ||
          partner?.companyName?.toLowerCase().includes(q);
        return idMatch || addrMatch || partnerNameMatch;
      });
    }

    // 3. Sorting
    result.sort((a, b) => {
      if (sortBy === "newest") {
        return Number(b.lotteryId || 0) - Number(a.lotteryId || 0);
      }
      if (sortBy === "oldest") {
        return Number(a.lotteryId || 0) - Number(b.lotteryId || 0);
      }
      if (sortBy === "highestPrize") {
        return compareWei(b.totalPrize, a.totalPrize);
      }
      if (sortBy === "lowestEntry") {
        return compareWei(a.entryFee, b.entryFee);
      }
      if (sortBy === "capacityHigh") {
        return Number(b.capacity || 0) - Number(a.capacity || 0);
      }
      if (sortBy === "capacityLow") {
        return Number(a.capacity || 0) - Number(b.capacity || 0);
      }
      if (sortBy === "pickHigh") {
        return Number(b.pickNumbers || 0) - Number(a.pickNumbers || 0);
      }
      return 0;
    });

    return result;
  }, [lotteryList, statusFilter, searchQuery, sortBy, partnerMap]);

  const hasActiveFilters = Boolean(searchQuery || statusFilter !== "all");

  const handleResetFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setSortBy("newest");
  };

  return (
    <Box className="mega-page-container">
      <NavBar />

      <Container maxWidth="xl" sx={{ pt: { xs: "96px", sm: "108px", md: "116px" }, pb: 6, flexGrow: 1 }}>
        {/* Hero Banner with Live On-Chain Metrics */}
        <MegaHeroBanner
          totalActive={totalActive}
          combinedPrize={combinedPrize}
          lowestEntry={lowestEntry}
          currency={currency}
        />

        {/* Filter & Search Bar */}
        <MegaLotteryFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onRefresh={() => fetchMegaLotteries(true)}
          loading={loading || refreshing}
          totalCount={totalActive}
          rolloverCount={rolloverCount}
          openCount={openCount}
        />

        {/* Loading Skeletons */}
        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((idx) => (
              <Grid item xs={12} sm={6} lg={4} key={idx}>
                <Box
                  sx={{
                    bgcolor: colors.dashboard.cardBg,
                    borderRadius: borderRadiusPx.xl,
                    border: `1px solid ${colors.dashboard.cardBorder}`,
                    p: 2.75,
                    height: 480,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    height={36}
                    sx={{ borderRadius: borderRadiusPx.md, mb: 2 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={80}
                    sx={{ borderRadius: borderRadiusPx.lg, mb: 2 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={72}
                    sx={{ borderRadius: borderRadiusPx.lg, mb: 2 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={90}
                    sx={{ borderRadius: borderRadiusPx.lg, mb: 2 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={36}
                    sx={{ borderRadius: borderRadiusPx.md, mt: "auto" }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : displayedLotteries.length === 0 ? (
          /* Empty State */
          <MegaLotteryEmptyState
            hasActiveFilters={hasActiveFilters}
            onReset={handleResetFilters}
          />
        ) : (
          /* MegaBET Cards Grid */
          <Grid container spacing={3}>
            {displayedLotteries.map((item) => {
              const partnerAddr = item.partnerAddress || item.partner;
              const partnerData = partnerMap[partnerAddr] || null;

              return (
                <Grid item xs={12} sm={6} lg={4} key={item.lotteryId}>
                  <MegaLotteryCard
                    lottery={item}
                    partner={partnerData}
                    partnerLogo={partnerLogoData}
                    currency={currency}
                  />
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* How It Works & FAQs Component */}
        <MegaHowToPlay />
      </Container>
    </Box>
  );
};

export default MegaLottery;
